<template>
  <div class="card-container">
    <div class="left-pane">
      <div class="header-row">
        <div class="left-info">
          <div class="price-refinfo-container">
            <div class="price-name">
              <span>{{ $str.getKey('收益管理/电价名称：') }}</span>
              <span>{{ props.priceInfo.elecPriceName }}</span>
            </div>
            <div class="create-time">
              <span>{{ $str.getKey('收益管理/有效时间：') }}</span>
              <span>{{ props.priceInfo.startDate }} ~ {{ props.priceInfo.endDate }}</span>
            </div>
          </div>
        </div>

        <div class="right-info">
          <el-button jd-type="primary-text" link :icon="Edit" :disabled="!canEditElectricPrice"
                     @click="handleAction('edit')">{{ $str.getKey('收益管理/编辑') }}</el-button>
        </div>
      </div>

      <div class="table-container">
        <div class="custom-table">
          <div class="t-header">
            <div class="tr">
              <div class="td">{{ $str.getKey('收益管理/开始时间') }}</div>
              <div class="td">{{ $str.getKey('收益管理/结束时间') }}</div>
              <div class="td">{{ $str.getKey('收益管理/时段名称') }}</div>
              <div class="td" v-if="props.priceInfo.chargeType === 0">{{ $str.getKey('收益管理/充电电价') }}</div>
              <div class="td" v-if="props.priceInfo.chargeType === 1">{{ $str.getKey('收益管理/放电电价') }}</div>
              <div class="td" v-if="props.priceInfo.chargeType === 2">{{ $str.getKey('收益管理/电价') }}</div>
            </div>
          </div>
          <div class="t-body-container">
            <div class="t-body">
              <div class="tr" v-for="price in props.priceInfo.priceDetails" :key="price"
                   :style="{ '--symbol-color': price.color }">
                <div class="td">{{ price.startTime }}</div>
                <div class="td">{{ price.endTime }}</div>
                <div class="td">
                  <div class="name-cell">
                    <div class="symbol-icon"></div>
                    <div class="name-text">{{ price.name }}</div>
                  </div>
                </div>
                <div class="td">{{ price.price }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="right-pane">
      <div class="symbols-container">
        <div class="item-container" v-for="symbol in symbolList" :key="symbol">
          <div class="symbol-icon" :style="{ '--bg-color': symbol.color }"></div>
          <div class="symbol-name">{{ symbol.name }}</div>
        </div>
      </div>
      <div class="chart-container" ref="linesChartDom"></div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, onMounted, onBeforeUnmount, computed } from "vue"
import { CaretRight, Edit, Delete } from '@element-plus/icons-vue';
import * as echarts from 'echarts';
import dayjs from 'dayjs'
import { checkPermi } from '@/utils/permission'
import { getMinutesCountByTime, getTimeByMinutesCount } from "@/utils/timeHelper/index.js"

const $str = window.$str;
const priceIcon = new URL('./assets/priceIcon.svg', import.meta.url).href;

const emit = defineEmits(['onEdit', 'onRecalculate', 'onStop', 'onDelete'])
const props = defineProps({
  priceInfo: {
    type: Object,
    required: true,
    default: function () { return {}; },
  },
})
const canEditElectricPrice = computed(() => checkPermi(['operate:electricPrice:edit']))

/** 图表-业务相关变量的定义 ====== */
const linesChartDom = ref(null);
let linesChartObj = null;
let chartResizeObserver = null;
let chartResizeRaf = 0;

const chartOptions = computed(() => {
  const infoItems = props.priceInfo?.priceDetails ?? [];
  const chartData = infoItems.map(item => {
    const st = getMinutesCountByTime(item.startTime)
    const et = getMinutesCountByTime(item.endTime)
    return {
      value: [st, et, item.price, item.name],
      itemStyle: {
        color: echarts.color.modifyAlpha(item.color, 0.5)
      }
    }
  })

  const lineColor = "#EDF2F6";
  const textColor = "#96A5B8";
  const option = {
    grid: {
      left: '5%',
      right: '3%',
      bottom: '10%',
      top: '15%',
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 1440,
      interval: 6 * 60,
      axisLabel: {
        color: textColor,
        formatter: (value) => getTimeByMinutesCount(value),
      },
      nameTextStyle: { color: textColor },
      splitLine: { show: true, lineStyle: { color: lineColor } }
    },
    yAxis: {
      type: 'value',
      name: $str.getKey('收益管理/价格(元/kWh)'),
      nameTextStyle: { color: textColor },
      axisLabel: { color: textColor },
      splitLine: { show: true, lineStyle: { color: lineColor } }
    },
    series: [
      {
        type: 'custom',
        data: chartData,
        renderItem: (params, api) => {
          const yValue = api.value(2)
          const start = api.coord([api.value(0), yValue])
          const size = api.size([api.value(1) - api.value(0), yValue]);
          return {
            type: 'rect',
            shape: {
              x: start[0],
              y: start[1],
              width: size[0],
              height: size[1] || 10,
            },
            style: size[1] ? api.style() : {
              fill: 'transparent',
              opacity: 0
            },
          }
        },
        label: { show: true, position: 'top' },
        dimensions: [$str.getKey('收益管理/开始时间'), $str.getKey('收益管理/结束时间'), $str.getKey('收益管理/电价'), $str.getKey('收益管理/电价名称')],
        encode: {
          x: [0, 1],
          y: 2,
          tooltip: [0, 1, 2],
          itemName: 3
        },
      }
    ]
  }
  option.tooltip = {
    show: true,
    trigger: 'item',
    axisPointer: { snap: false },
    className: 'chart-tooltip',
    backgroundColor: '#ffffff',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    hideDelay: 0,
    enterable: false,
    formatter: (params) => {
      params = params || params[0]
      if (!params) return
      const start_time = getTimeByMinutesCount(params.value[0])
      const end_time = getTimeByMinutesCount(params.value[1])
      const price = params.value[2]
      return /* html*/`
          <div class="chart-tooltip__time">
            <div class="chart-tooltip__point" style="background: ${params.color}"></div>
            ${params.name}
          </div>
          <div class="chart-tooltip__items-container">
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/开始时间')}</div>
              <div class="chart-tooltip__value">${start_time}</div>
            </div>
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/结束时间')}</div>
              <div class="chart-tooltip__value">${end_time}</div>
            </div>
            <div class="chart-tooltip__series">
              <div class="chart-tooltip__series-name">${$str.getKey('收益管理/电价')}</div>
              <div class="chart-tooltip__value">${price}</div>
            </div>
          </div>
        `
    }
  }
  return option;
})

const symbolList = computed(() => {
  const resList = [];
  if (Array.isArray(props?.priceInfo?.priceDetails)) {
    props.priceInfo.priceDetails.forEach(it => {
      if (!resList.some(symbol => symbol.name === it.name)) {
        resList.push({ name: it.name, color: it.color });
      }
    })
  }
  return resList;
})

function handleWindowResize() {
  linesChartObj && linesChartObj.resize();
}

onMounted(() => {
  linesChartObj = echarts.init(linesChartDom.value);
  linesChartObj.setOption(chartOptions.value);

  if (linesChartDom.value && typeof ResizeObserver !== 'undefined') {
    chartResizeObserver = new ResizeObserver(() => {
      if (chartResizeRaf) cancelAnimationFrame(chartResizeRaf);
      chartResizeRaf = requestAnimationFrame(() => {
        linesChartObj && linesChartObj.resize();
      });
    });
    chartResizeObserver.observe(linesChartDom.value);
  }
  window.addEventListener('resize', handleWindowResize);
})

onBeforeUnmount(() => {
  if (chartResizeObserver) {
    chartResizeObserver.disconnect();
    chartResizeObserver = null;
  }
  if (chartResizeRaf) cancelAnimationFrame(chartResizeRaf);
  window.removeEventListener('resize', handleWindowResize);
  if (linesChartObj) {
    linesChartObj.dispose();
    linesChartObj = null;
  }
})

function formatPriceValueForShow(price) {
  if (typeof (price) === 'number' || isNaN(price)) {
    return (+price).toFixed(2);
  } else {
    return price
  }
}

function handleAction(action) {
  if (action === 'edit' && !canEditElectricPrice.value) return
  const actionType = 'on' + action.slice(0, 1).toUpperCase() + action.slice(1)
  emit(actionType)
}
</script>

<style lang="scss" scoped>
/* ============================================================
   响应式重构：
   - 不再使用任何固定 px 容器宽度;不再使用 calc(100% - 固定值)
   - 用 CSS Grid 控制左右两栏比例,minmax(0, 1fr) 允许子项收缩
   - 窄屏 (< 1280px) 自动堆叠为上下两栏
   - echarts 通过 ResizeObserver 跟随容器尺寸变化
   ============================================================ */

.card-container {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 360px;
  padding: 20px;
  box-sizing: border-box;
  border: none;
  overflow: hidden;

  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr);
  gap: 16px;
}

/* ====== 左栏:表头 + 表格 ====== */
.left-pane {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;

  .left-info {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    flex: 1 1 auto;

    .price-refinfo-container {
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;

      .price-name {
        color: #444A6D;
        font-feature-settings: 'liga' off, 'clig' off;
        font-family: "HarmonyOS Sans SC";
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
        overflow-wrap: anywhere;
      }
      .create-time {
        color: rgba(0, 0, 0, 0.50);
        font-feature-settings: 'liga' off, 'clig' off;
        font-family: "HarmonyOS Sans SC";
        font-size: 12px;
        font-weight: 300;
        line-height: normal;
        overflow-wrap: anywhere;
      }
    }
  }

  .right-info {
    flex-shrink: 0;

    :deep(.el-button) {
      color: #2E2E2E !important;
      font-family: "HarmonyOS Sans SC" !important;
      font-size: 14px !important;
      font-weight: 400 !important;
      line-height: normal !important;

      .el-icon { color: #2E2E2E !important; }
      span { color: #2E2E2E !important; }
    }
  }
}

.table-container {
  width: 100%;
  min-width: 0;
  flex: 1;
  margin-top: 12px;
  box-sizing: border-box;
  overflow: hidden;

  .custom-table {
    --cus-tr-h: 32px;
    --cus-tr-gap: 6px;
    --cus-tr-radius: 4px;

    width: 100%;
    min-width: 0;
    height: 100%;
    display: flex;
    flex-direction: column;

    .t-body-container {
      flex: 1;
      min-height: 0;
      overflow-x: hidden;
      overflow-y: auto;
      padding-right: 4px;
    }

    .t-body-container::-webkit-scrollbar {
      width: 6px;
    }

    .t-body-container::-webkit-scrollbar-thumb {
      border-radius: 999px;
      background: rgba(150, 165, 184, 0.35);
    }

    .t-body-container::-webkit-scrollbar-track {
      background: transparent;
    }

    .t-header, .t-body {
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: var(--cus-tr-gap);
      box-sizing: border-box;
      font-size: 14px;

      .tr {
        width: 100%;
        min-width: 0;
        height: var(--cus-tr-h);
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        align-items: center;
        border-radius: var(--cus-tr-radius);
        background: var(--level3-bg-color);
        position: relative;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          width: 100%;
          height: 1px;
          background: #EDF2F6;
        }
        &:last-child::after { display: none; }

        .td {
          min-width: 0;
          padding-left: 10px;
          display: flex;
          align-items: center;
          justify-content: flex-start;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }

    .t-header {
      margin: var(--cus-tr-gap) 0;
      color: #96A5B8;
      font-family: "HarmonyOS Sans SC";
      font-size: 13px;
      font-weight: 400;
      line-height: 16px;

      .tr .td {
        color: #96A5B8;
        font-family: "HarmonyOS Sans SC";
        font-size: 13px;
        font-weight: 400;
        line-height: 16px;
      }
    }

    .t-body {
      .tr:hover,
      .tr:hover .td {
        background-color: var(--el-table-row-hover-bg-color-jd);
      }

      .tr .td {
        color: #444A6D;
        font-feature-settings: 'liga' off, 'clig' off;
        font-family: "HarmonyOS Sans SC";
        font-size: 14px;
        font-weight: 400;
        line-height: normal;
      }

      .name-cell {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;

        .symbol-icon {
          box-sizing: border-box;
          width: 12px;
          height: 12px;
          flex-shrink: 0;
          border: 3px solid var(--symbol-color);
          border-radius: 50%;
        }
        .name-text {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
}

/* ====== 右栏:symbol + echarts 图表 ====== */
.right-pane {
  position: relative;
  min-width: 0;
  min-height: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.symbols-container {
  width: 100%;
  padding: 0 8px;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  min-height: 20px;

  .item-container {
    display: flex;
    align-items: center;
    gap: 4px;

    .symbol-icon {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background-color: var(--bg-color);
      flex-shrink: 0;
    }
    .symbol-name {
      font-size: 12px;
      color: #96A5B8;
      font-family: "HarmonyOS Sans SC";
      white-space: nowrap;
    }
  }
}

.chart-container {
  width: 100%;
  min-width: 0;
  flex: 1;
  min-height: 0;
  margin-top: 8px;
}

/* ====== 窄屏自适应:保持左右两栏布局,压缩比例并降低固定高度 ======
   1366/1440 视口减侧边栏后容器约 1080~1180px,此时仍保持左右,但右侧图表区比例降低,
   并取消固定 360px 改用弹性高度,避免横向被两栏内容硬撑而需要横向滚动
*/
@media (max-width: 1440px) {
  .card-container {
    height: auto;
    min-height: 300px;
    grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
    gap: 12px;
    padding: 16px;
  }
  .right-pane {
    min-height: 220px;
  }
  .chart-container {
    min-height: 180px;
  }
}

@media (max-width: 1100px) {
  .card-container {
    grid-template-columns: minmax(0, 1.8fr) minmax(0, 1fr);
    gap: 10px;
    padding: 12px;
  }
}
</style>

<style lang="scss">
/* echarts tooltip 全局样式,保持原样 */
.chart-tooltip {
  padding: 12px 16px !important;
  border-radius: 8px !important;
}
.chart-tooltip__time {
  margin-bottom: 8px;
  font-family: "HarmonyOS Sans SC";
  font-size: 14px;
  display: flex;
  align-items: center;
}
.chart-tooltip__items-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.chart-tooltip__series {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 6px 10px;
  border-radius: 6px;
}
.chart-tooltip__point {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 6px;
}
.chart-tooltip__series-name {
  font-family: "HarmonyOS Sans SC";
  font-size: 13px;
  margin-right: 8px;
}
.chart-tooltip__value {
  font-family: "HarmonyOS Sans SC";
  font-size: 14px;
  font-weight: 600;
}
</style>
